import classNames from "classnames";
import { Checkbox } from "components/Checkbox/Checkbox";
import { ButtonPrimary, ButtonSecondary } from "components/Clickable/Buttons";
import { useRouterParams } from "components/useRouterParams";
import { AttributeOption } from "interfaces/products";
import { useRouter } from "next/router";
import CaretIcon from "public/svg/caret.svg";
import XIcon from "public/svg/x.svg";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import typographyStyles from "styles/typography.module.scss";

import { slugify, useWindowWidth } from "../../helpers/utils";

import sassVariables from "../../styles/variables.module.scss";
import styles from "./grid-filters.module.scss";

type FilterAttribute = {
    id: number;
    name: string;
    pim_id: number;
    singular_name: string;
};

type OptionType = {
    id: number;
    name: string;
    display_name: string;
};

export type Filter = {
    attribute: FilterAttribute;
    display_name: string;
    is_featured: boolean;
    option_type: OptionType | null;
    options: AttributeOption[];
    show_label: boolean;
};

type FilterWithStatus = Filter & {
    isOpen: boolean;
    showAll: boolean;
};

type GridFiltersProps = {
    filters: Filter[];
    nonzeroFilterIDs?: any;
    isMobile?: boolean;
    setShowMobileFilters?: (status: boolean) => void;
};

export const getUniqueFilters = (filters: AttributeOption[]) => {
    return filters.filter((option: AttributeOption, index, self) => {
        if (option.option === "") return false;
        return index === self.findIndex((o) => o.option === option.option);
    });
};

export const GridFilters = ({
    filters,
    isMobile = false,
    nonzeroFilterIDs = null,
    setShowMobileFilters = () => {},
}: GridFiltersProps) => {
    const filtersWithStatus = filters.map((filter: Filter) => {
        return { ...filter, isOpen: false, showAll: false };
    });

    const [filterGroups, setFilterGroups] = useState(filtersWithStatus);
    const formRef = useRef<HTMLFormElement>(null);
    const router = useRouter();
    const windowWidth = useWindowWidth();
    const routerParams = useRouterParams({
        method: "push",
        shallow: true,
        scroll: false,
    });
    const closeMobileFilters = useCallback(
        () => setShowMobileFilters(false),
        [setShowMobileFilters],
    );

    useEffect(() => {
        if (
            windowWidth &&
            windowWidth < parseInt(sassVariables.breakpointMobile)
        )
            closeMobileFilters();
    }, [windowWidth, closeMobileFilters]);

    const updateFilterGroups = (id: string) => {
        const newFilterGroups = [...filterGroups];
        const targetFilterGroup = newFilterGroups.find(
            (filterGroup) => filterGroup.attribute.id === Number(id),
        );
        if (targetFilterGroup)
            targetFilterGroup.isOpen = !targetFilterGroup.isOpen;
        setFilterGroups(newFilterGroups);
    };

    const handleShowAll = (id: string) => {
        const newFilterGroups = [...filterGroups];
        const targetFilterGroup = newFilterGroups.find(
            (filterGroup) => filterGroup.attribute.id === Number(id),
        );
        if (targetFilterGroup)
            targetFilterGroup.showAll = !targetFilterGroup.showAll;
        setFilterGroups(newFilterGroups);
    };

    const onFilterChange = (event: FormEvent<HTMLInputElement>) => {
        const filterIDName = event.currentTarget.name;
        const filterOptionID = event.currentTarget.value;

        delete router.query.offset;
        routerParams.toggleParam(filterIDName, filterOptionID);
    };

    const getQueryParamKeys = () => {
        const queryKeys = [...Object.keys(router.query)];

        return queryKeys.filter((key) => key !== "slug" && key !== "ordering");
    };

    const onClearFilters = () => {
        // Reset all checkboxes
        document
            .querySelectorAll("input[type=checkbox]")
            .forEach((el) => (el.ariaChecked = "false"));

        const queryParamKeys = getQueryParamKeys();

        if (queryParamKeys.length != 0) {
            routerParams.clearParams(...queryParamKeys);
        }
    };

    const willReturnZero = (attributeID: number, optionID: number) => {
        if (!nonzeroFilterIDs) {
            return false;
        }

        const hasOptionID = nonzeroFilterIDs[attributeID].includes(optionID);

        if (hasOptionID) {
            return false;
        }

        return true;
    };

    const isFilterIDInParams = (attributeID: number) => {
        const queryKeys = [...Object.keys(router.query)];

        return queryKeys.includes(attributeID.toString());
    };

    const countFilters = (filterID: string) => {
        const filterIDValues = routerParams.getParamValue(filterID);

        if (filterIDValues) {
            if (typeof filterIDValues === "string") {
                return "(1)";
            } else {
                return `(${filterIDValues.length})`;
            }
        }

        return null;
    };

    const renderFilter = (filter: FilterWithStatus) => {
        // Don't show Filter Attributes that contain no option_types
        if (filter.options.length === 0) {
            return null;
        }

        const allUniqueFilters = getUniqueFilters(filter.options);
        const uniqueFilters = filter.showAll
            ? allUniqueFilters
            : allUniqueFilters.slice(0, 9);

        return (
            <div key={filter.attribute.id} className={styles.filterGroup}>
                <div
                    className={`${
                        styles.filterGroupName
                    } al-grid-rail__filter-group al-grid-rail__filter-group--${slugify(
                        filter.display_name,
                    )}`}
                    onClick={() =>
                        updateFilterGroups(filter.attribute.id.toString())
                    }
                >
                    <span>
                        {filter.display_name}{" "}
                        {countFilters(filter.attribute.id.toString())}
                    </span>
                    <CaretIcon
                        className={classNames({
                            [styles.caretIcon]: true,
                            [styles.caretIconOpen]: filter.isOpen,
                        })}
                    />
                </div>

                {filter.isOpen && (
                    <div className={styles.filterOptions}>
                        <ul>
                            {uniqueFilters.map((option: AttributeOption) => (
                                <li
                                    className={classNames({
                                        [styles.filterDisabled]:
                                            willReturnZero(
                                                filter.attribute.id,
                                                option.id,
                                            ) &&
                                            !isFilterIDInParams(
                                                filter.attribute.id,
                                            ),
                                        ["al-grid-rail__filter"]: true,
                                        [`al-grid-rail__filter--${slugify(
                                            option.option,
                                        )}`]: true,
                                    })}
                                    key={option.id}
                                >
                                    <Checkbox
                                        value={option.id.toString()}
                                        name={filter.attribute.id.toString()}
                                        onChange={onFilterChange}
                                        checked={routerParams.hasParam(
                                            filter.attribute.id.toString(),
                                            option.id.toString(),
                                        )}
                                    >
                                        {option.option}
                                    </Checkbox>
                                </li>
                            ))}
                        </ul>
                        {filter.options.length > 9 && !filter.showAll && (
                            <button
                                className={styles.ctaViewAll}
                                onClick={() =>
                                    handleShowAll(
                                        filter.attribute.id.toString(),
                                    )
                                }
                            >
                                View All
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`${isMobile && styles.gridFiltersMobile}`}>
            <div className={styles.header}>
                <span className={typographyStyles.primaryAllCaps}>
                    Filter by
                </span>
                {!isMobile && getQueryParamKeys().length > 0 && (
                    <button className={styles.cta} onClick={onClearFilters}>
                        Clear All
                    </button>
                )}
                {isMobile && (
                    <XIcon
                        className={styles.xIcon}
                        onClick={closeMobileFilters}
                    />
                )}
            </div>
            <form ref={formRef} className={styles.filterForm}>
                {filterGroups.map((filterGroup: FilterWithStatus) => {
                    return renderFilter(filterGroup);
                })}
            </form>
            {isMobile && (
                <div className={styles.buttonsMobile}>
                    <ButtonSecondary onClick={() => onClearFilters()}>
                        Clear
                    </ButtonSecondary>
                    <ButtonPrimary onClick={closeMobileFilters}>
                        Apply
                    </ButtonPrimary>
                </div>
            )}
        </div>
    );
};
